import { useCallback, useState } from 'react';
import Icon from '../Icon';
import Shimmer from '../Shimmer';
import classnames from 'classnames';
import { formatNumber } from '@/utils/helpers';

type OrderSummaryPanelProps = {
  subTotal: number;
  membershipDiscountValue: number;
  programsMembershipDiscountValue: number;
  publicationsMembershipDiscountValue: number;
  otherDiscountValue: number;
  shippingCost: number;
  taxTotal: number;
  total: number;
  isLoading?: boolean;
  isExpandable?: boolean;
  isShippingRequired: boolean;
  isCartEmpty?: boolean;
  classNames?: string;
  isShippingAddressSelected?: boolean;
};

const OrderSummaryPanel: React.FC<OrderSummaryPanelProps> = ({
  subTotal,
  programsMembershipDiscountValue,
  publicationsMembershipDiscountValue,
  otherDiscountValue,
  shippingCost,
  taxTotal,
  total,
  isLoading,
  isExpandable,
  isShippingRequired,
  isCartEmpty = false,
  isShippingAddressSelected = true,
  classNames
}) => {
  const [isExpanded, setIsExpanded] = useState(!isExpandable);
  const toggleExpandPanel = useCallback(() => setIsExpanded(state => !state), [setIsExpanded]);

  return (
    <section
      className={classnames(
        'text-1 flex h-fit flex-col gap-8 rounded bg-white px-5 py-[18px] sm:px-8 sm:py-10',
        classNames
      )}
    >
      {isExpandable ? (
        <button onClick={toggleExpandPanel} className="flex justify-between">
          <span className="font-bold">Order Summary</span>
          <Icon name="chevron-down" size="medium" />
        </button>
      ) : (
        <div className="font-bold">Order Summary</div>
      )}
      {isLoading && isExpanded ? (
        <Shimmer className="h-20 w-full" />
      ) : (
        isExpanded && (
          <>
            <div className="flex flex-col gap-[9px]">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{isCartEmpty ? '-' : `$${formatNumber(subTotal)}`}</span>
              </div>
              {programsMembershipDiscountValue > 0 && (
                <div className="flex justify-between text-gray">
                  <div className="flex flex-row gap-1">
                    <Icon name="checkmark" size="small" />
                    <span className="label">Member savings</span>
                  </div>
                  <span>-${formatNumber(programsMembershipDiscountValue)}</span>
                </div>
              )}
              {publicationsMembershipDiscountValue > 0 && (
                <div className="flex justify-between text-gray">
                  <div className="flex flex-row gap-1">
                    <Icon name="checkmark" size="small" />
                    <span className="label">PLI PLUS savings</span>
                  </div>
                  <span>-${formatNumber(publicationsMembershipDiscountValue)}</span>
                </div>
              )}
              {otherDiscountValue > 0 && (
                <div className="flex justify-between text-gray">
                  <div className="flex flex-row gap-1">
                    <Icon name="checkmark" size="small" />
                    <span className="label">Discounts</span>
                  </div>
                  <span>-${formatNumber(otherDiscountValue)}</span>
                </div>
              )}
              {(isShippingRequired || isCartEmpty) && (
                <div className="flex justify-between">
                  <span>Shipping & Handling</span>
                  <span>
                    {!isShippingAddressSelected || isCartEmpty
                      ? '-'
                      : `$${formatNumber(shippingCost ?? 0)}`}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Estimated Applicable Tax</span>
                <span>
                  {!isShippingAddressSelected || isCartEmpty
                    ? '-'
                    : `$${formatNumber(taxTotal ?? 0)}`}
                </span>
              </div>
            </div>
            <div className="flex justify-between border-t pt-6 font-bold">
              <span>Total</span>
              <span>{isCartEmpty ? '-' : `$${formatNumber(total ?? 0)}`}</span>
            </div>
          </>
        )
      )}
    </section>
  );
};

export default OrderSummaryPanel;
